/**
 * Cartoon goat mascot — inline SVG with CSS animations.
 *
 * Animates: subtle breathing on the body, tail wag, occasional ear flick,
 * occasional eye blink, gentle leg bob, and a swinging bell. Animations
 * are disabled when the user has `prefers-reduced-motion: reduce` set.
 *
 * Lightweight (~3KB inline) and crisp at any size — preferable to a GIF.
 */
export function GoatMascot({
  className,
  ariaLabel,
}: {
  className?: string;
  ariaLabel?: string;
}): JSX.Element {
  return (
    <svg
      viewBox="0 0 240 180"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={ariaLabel ?? 'Cabra mascote do GOPINHOS'}
    >
      <style>{`
        .goat-body { animation: goat-breathe 2.6s ease-in-out infinite; transform-origin: 110px 115px; transform-box: fill-box; }
        .goat-head { animation: goat-nod 3.2s ease-in-out infinite; transform-origin: 170px 95px; transform-box: fill-box; }
        .goat-tail { animation: goat-tail-wag 1.4s ease-in-out infinite; transform-origin: 100% 50%; transform-box: fill-box; }
        .goat-ear { animation: goat-ear-flick 5.4s ease-in-out infinite; transform-origin: 50% 100%; transform-box: fill-box; }
        .goat-ear-r { animation-delay: -2.2s; }
        .goat-eye { animation: goat-blink 4.6s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
        .goat-bell { animation: bell-swing 1.4s ease-in-out infinite; transform-origin: 50% 0%; transform-box: fill-box; }
        .goat-leg-front { animation: leg-step 1.2s ease-in-out infinite; transform-origin: 50% 0%; transform-box: fill-box; }
        .goat-leg-back { animation: leg-step 1.2s ease-in-out infinite; animation-delay: -0.6s; transform-origin: 50% 0%; transform-box: fill-box; }

        @keyframes goat-breathe {
          0%, 100% { transform: translateY(0) scale(1); }
          50%      { transform: translateY(-2px) scale(1.015); }
        }
        @keyframes goat-nod {
          0%, 100% { transform: rotate(-2deg) translateY(0); }
          50%      { transform: rotate(2deg) translateY(-1px); }
        }
        @keyframes goat-tail-wag {
          0%, 100% { transform: rotate(-22deg); }
          50%      { transform: rotate(22deg); }
        }
        @keyframes goat-ear-flick {
          0%, 88%, 100% { transform: rotate(0deg); }
          92%           { transform: rotate(-18deg); }
          96%           { transform: rotate(0deg); }
        }
        @keyframes goat-blink {
          0%, 91%, 100% { transform: scaleY(1); }
          94%, 96%      { transform: scaleY(0.08); }
        }
        @keyframes bell-swing {
          0%, 100% { transform: rotate(-10deg); }
          50%      { transform: rotate(10deg); }
        }
        @keyframes leg-step {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-2.5px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .goat-body, .goat-head, .goat-tail, .goat-ear,
          .goat-eye, .goat-bell, .goat-leg-front, .goat-leg-back {
            animation: none;
          }
        }
      `}</style>

      {/* Soft ground shadow */}
      <ellipse cx="120" cy="172" rx="78" ry="5" fill="#1c3a48" opacity="0.10" />

      {/* Tail — drawn before body so it tucks behind */}
      <g className="goat-tail">
        <path d="M 60 100 Q 45 96 42 88 L 50 95 Q 56 99 62 102 Z" fill="#fafaf7" stroke="#aba896" strokeWidth="1" strokeLinejoin="round" />
        <circle cx="46" cy="92" r="3.5" fill="#fafaf7" stroke="#aba896" strokeWidth="1" />
      </g>

      {/* Back legs (drawn first so front legs overlap) */}
      <g className="goat-leg-back">
        <rect x="76" y="138" width="9" height="30" rx="3" fill="#f4f3ed" stroke="#aba896" strokeWidth="1" />
        <rect x="76" y="160" width="9" height="8" rx="2" fill="#46443c" />
      </g>
      <g className="goat-leg-back" style={{ animationDelay: '-0.3s' }}>
        <rect x="92" y="140" width="9" height="28" rx="3" fill="#e9e7dc" stroke="#aba896" strokeWidth="1" />
        <rect x="92" y="160" width="9" height="8" rx="2" fill="#2e2d28" />
      </g>

      {/* Front legs */}
      <g className="goat-leg-front">
        <rect x="136" y="138" width="9" height="30" rx="3" fill="#f4f3ed" stroke="#aba896" strokeWidth="1" />
        <rect x="136" y="160" width="9" height="8" rx="2" fill="#46443c" />
      </g>
      <g className="goat-leg-front" style={{ animationDelay: '-0.6s' }}>
        <rect x="152" y="140" width="9" height="28" rx="3" fill="#e9e7dc" stroke="#aba896" strokeWidth="1" />
        <rect x="152" y="160" width="9" height="8" rx="2" fill="#2e2d28" />
      </g>

      {/* Body */}
      <g className="goat-body">
        <ellipse cx="110" cy="115" rx="55" ry="32" fill="#fafaf7" stroke="#aba896" strokeWidth="1.5" />
        {/* Belly accent */}
        <ellipse cx="115" cy="128" rx="40" ry="14" fill="#f4f3ed" />
        {/* Spot detail */}
        <ellipse cx="88" cy="103" rx="9" ry="6" fill="#e9e7dc" opacity="0.8" />
        <ellipse cx="125" cy="98" rx="6" ry="4" fill="#e9e7dc" opacity="0.7" />

        {/* Bell on collar */}
        <line x1="148" y1="103" x2="158" y2="105" stroke="#1c4759" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="138" y1="105" x2="148" y2="103" stroke="#1c4759" strokeWidth="1.5" strokeLinecap="round" />
        <g className="goat-bell">
          <path d="M 145 102 L 152 102 L 151 110 Q 148.5 113 146 110 Z" fill="#e7ad29" stroke="#a36818" strokeWidth="0.8" strokeLinejoin="round" />
          <circle cx="148.5" cy="111" r="1.2" fill="#834f1a" />
        </g>
      </g>

      {/* Head group — sits on top right of body */}
      <g className="goat-head">
        {/* Ears (behind head) */}
        <g className="goat-ear">
          <ellipse cx="156" cy="68" rx="6" ry="11" fill="#f4f3ed" stroke="#aba896" strokeWidth="1.2" transform="rotate(-30 156 68)" />
          <ellipse cx="156" cy="70" rx="3" ry="6" fill="#efc54f" opacity="0.4" transform="rotate(-30 156 70)" />
        </g>
        <g className="goat-ear goat-ear-r">
          <ellipse cx="184" cy="68" rx="6" ry="11" fill="#fafaf7" stroke="#aba896" strokeWidth="1.2" transform="rotate(30 184 68)" />
          <ellipse cx="184" cy="70" rx="3" ry="6" fill="#efc54f" opacity="0.4" transform="rotate(30 184 70)" />
        </g>

        {/* Horns */}
        <path d="M 162 60 Q 158 50 161 42 Q 165 48 166 58 Z" fill="#5d5a4f" stroke="#46443c" strokeWidth="1" strokeLinejoin="round" />
        <path d="M 178 60 Q 182 50 179 42 Q 175 48 174 58 Z" fill="#46443c" stroke="#2e2d28" strokeWidth="1" strokeLinejoin="round" />

        {/* Head — main shape */}
        <ellipse cx="170" cy="88" rx="22" ry="22" fill="#fafaf7" stroke="#aba896" strokeWidth="1.5" />
        {/* Snout */}
        <ellipse cx="184" cy="100" rx="14" ry="11" fill="#f4f3ed" stroke="#aba896" strokeWidth="1.2" />

        {/* Eye */}
        <ellipse className="goat-eye" cx="172" cy="83" rx="2.6" ry="3.2" fill="#1c1b18" />
        <circle cx="172.8" cy="82" r="0.9" fill="white" />

        {/* Nose */}
        <ellipse cx="192" cy="98" rx="3" ry="2" fill="#46443c" />

        {/* Mouth — tiny smile */}
        <path d="M 188 103 Q 184 105 181 103" stroke="#46443c" strokeWidth="1" fill="none" strokeLinecap="round" />

        {/* Beard — hanging wisp */}
        <path d="M 178 110 Q 176 116 178 121 Q 180 116 178 110 M 182 110 Q 184 117 182 124 Q 180 117 182 110"
              fill="#fafaf7" stroke="#aba896" strokeWidth="1" strokeLinejoin="round" />
        <path d="M 175 109 L 173 121 L 178 116 L 180 124 L 184 117 L 187 121 L 184 110 Z"
              fill="#fafaf7" stroke="#aba896" strokeWidth="1.2" strokeLinejoin="round" />

        {/* Cheek blush */}
        <ellipse cx="160" cy="95" rx="4" ry="2" fill="#efc54f" opacity="0.35" />
      </g>
    </svg>
  );
}

/**
 * Full-width walking strip — one or more goats stroll left → right across
 * the page, turn around at each edge, and loop. Each goat can have its
 * own size, vertical offset (depth-of-field cheat), and animation phase.
 *
 * Drop this at the bottom of a `relative` container with `overflow-x: hidden`
 * on the ancestor that owns the viewport.
 */
export interface WalkingGoatConfig {
  /** Tailwind width class — controls perceived distance (bigger = closer). */
  sizeClass?: string;
  /** Negative seconds shift the goat ahead in the cycle; positive lag. */
  delaySeconds?: number;
  /** Tailwind bottom-X class — slight vertical offset gives a parallax feel. */
  bottomClass?: string;
}

const DEFAULT_HERD: WalkingGoatConfig[] = [
  { sizeClass: 'w-64', delaySeconds: 0,  bottomClass: 'bottom-1' }, // largest, foreground
  { sizeClass: 'w-52', delaySeconds: -2, bottomClass: 'bottom-3' }, // mid
  { sizeClass: 'w-44', delaySeconds: -4, bottomClass: 'bottom-6' }, // smallest, "further away"
];

export function WalkingGoat({
  className,
  /** One or more goat configurations. Defaults to a 3-goat herd. */
  goats = DEFAULT_HERD,
  /** Seconds for one full there-and-back trip across the page. */
  durationSeconds = 32,
}: {
  className?: string;
  goats?: WalkingGoatConfig[];
  durationSeconds?: number;
}): JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-x-0 bottom-0 h-44 overflow-hidden ${className ?? ''}`}
    >
      <style>{`
        .goat-walker {
          animation: goat-stroll ${durationSeconds}s ease-in-out infinite;
          will-change: transform;
        }
        @keyframes goat-stroll {
          0%   { transform: translateX(-4%) scaleX(1); }
          46%  { transform: translateX(calc(100vw - 100% + 4%)) scaleX(1); }
          50%  { transform: translateX(calc(100vw - 100% + 4%)) scaleX(-1); }
          96%  { transform: translateX(-4%) scaleX(-1); }
          100% { transform: translateX(-4%) scaleX(1); }
        }
        /* Desync the inner SVG animations between herd members so they don't
           breathe / wag / blink in unison. */
        .goat-walker:nth-child(2) .goat-body { animation-delay: -0.7s; }
        .goat-walker:nth-child(2) .goat-tail { animation-delay: -0.4s; }
        .goat-walker:nth-child(2) .goat-bell { animation-delay: -0.6s; }
        .goat-walker:nth-child(2) .goat-eye  { animation-delay: -1.8s; }
        .goat-walker:nth-child(2) .goat-leg-front { animation-delay: -0.3s; }
        .goat-walker:nth-child(2) .goat-leg-back  { animation-delay: -0.9s; }

        .goat-walker:nth-child(3) .goat-body { animation-delay: -1.4s; }
        .goat-walker:nth-child(3) .goat-tail { animation-delay: -0.9s; }
        .goat-walker:nth-child(3) .goat-bell { animation-delay: -1.1s; }
        .goat-walker:nth-child(3) .goat-eye  { animation-delay: -3.1s; }
        .goat-walker:nth-child(3) .goat-leg-front { animation-delay: -0.6s; }
        .goat-walker:nth-child(3) .goat-leg-back  { animation-delay: 0s; }

        @media (prefers-reduced-motion: reduce) {
          .goat-walker { animation: none; }
        }
      `}</style>
      {goats.map((g, i) => (
        <div
          key={i}
          className={`goat-walker absolute left-0 ${g.bottomClass ?? 'bottom-2'} ${g.sizeClass ?? 'w-56'}`}
          style={{ animationDelay: `${g.delaySeconds ?? 0}s` }}
        >
          <GoatMascot />
        </div>
      ))}
    </div>
  );
}

